import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, ArrowRight, Mail, Phone, Globe, Shield } from 'lucide-react';
import Seo from '@/seo/Seo';
import { breadcrumbSchema } from '@/seo/schemas';
import { CHURCH } from '@/api/client';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

/**
 * The FAQ page currently covers photography, video and live streaming.
 * More categories can be appended later without changing the layout —
 * add another object to CATEGORIES below.
 */
const CATEGORIES = [
  {
    id: 'photography',
    label: 'Photography, Video & Live Streaming',
    icon: Camera,
    intro:
      "Ministry of Jesus takes privacy, safeguarding and the responsible use of church media seriously. These questions cover how we handle photography, video and live streaming across our services and events.",
    items: [
      {
        q: 'Are church services recorded or live-streamed?',
        a: `Selected services, worship gatherings, conferences, and special events may be photographed, recorded, or live-streamed through the Ministry of Jesus website, YouTube channel, and authorised social-media platforms. Notices will be displayed whenever recording or live streaming takes place.`,
      },
      {
        q: 'Does entering the church automatically give media consent?',
        a: `Entrance signage provides notification that photography or recording may take place. Attendance alone does not automatically replace any specific consent required for individually focused photographs or promotional media.`,
      },
      {
        q: 'What happens when camera appearance is not preferred?',
        a: `A member of the Media Team may be contacted before the service or event. Reasonable arrangements will be provided wherever practicable to reduce prominent appearance in photographs or video recordings.`,
      },
      {
        q: 'Can children be photographed or recorded?',
        a: `Additional safeguards apply to children and young people. Identifiable photographs or video intended for public church communications will normally require appropriate written consent from a parent or legal guardian.`,
      },
      {
        q: 'Where can church photographs and videos appear?',
        a: [
          'Approved church media may appear on:',
          '• Ministry of Jesus website',
          '• Official YouTube channel',
          '• Facebook',
          '• Instagram',
          '• Church newsletters',
          '• Event publicity',
          '• Printed church materials',
          '• Other authorised Ministry of Jesus communication platforms',
        ].join('\n'),
      },
      {
        q: 'What happens without parental media consent?',
        a: `Reasonable steps will be taken to prevent a child from being intentionally photographed, prominently recorded, individually featured, or identified in public-facing church media.`,
      },
      {
        q: 'Can parental media consent be withdrawn?',
        a: `Yes. A parent or legal guardian may contact Ministry of Jesus to withdraw permission for future use of identifiable photographs or recordings. Withdrawal applies to future use wherever reasonably practicable.`,
      },
      {
        q: 'Who handles privacy or media concerns?',
        a: `Privacy, photography, video, live-streaming, or safeguarding enquiries may be submitted through the contact details listed at the bottom of this page.`,
      },
    ],
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: CATEGORIES.flatMap((c) =>
    c.items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    }))
  ),
};

export default function FAQ() {
  return (
    <div>
      <Seo
        title="FAQ | Ministry of Jesus, Dublin"
        titleExact
        description="Frequently asked questions about photography, video recording, live streaming, media consent and privacy at Ministry of Jesus, Dublin."
        path="/faq"
        keywords={[
          'Ministry of Jesus FAQ',
          'church photography policy',
          'live streaming consent Ireland',
          'children media consent',
        ]}
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'FAQ', path: '/faq' },
          ]),
          faqJsonLd,
        ]}
      />

      <section className="gradient-purple text-white py-10 sm:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-moj-gold text-xs font-bold uppercase tracking-wider mb-5 border border-moj-gold/30">
            <Camera size={14} /> Frequently Asked Questions
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight mb-3">
            FAQ
          </h1>
          <p className="text-white/85 max-w-2xl">
            Everything you need to know about photography, video and live streaming
            across the life of the church.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-moj-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-12">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <div key={cat.id} data-testid={`faq-category-${cat.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl gradient-purple flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-moj-gold" />
                  </div>
                  <h2 className="font-serif text-2xl sm:text-3xl text-moj-purple-deep m-0">
                    {cat.label}
                  </h2>
                </div>
                {cat.intro && (
                  <p className="text-moj-charcoal/80 text-base leading-relaxed mb-5">
                    {cat.intro}
                  </p>
                )}
                <Accordion
                  type="single"
                  collapsible
                  className="space-y-2"
                  data-testid={`faq-accordion-${cat.id}`}
                >
                  {cat.items.map((it, i) => (
                    <AccordionItem
                      key={i}
                      value={`${cat.id}-${i}`}
                      className="border border-moj-gold/30 rounded-xl bg-white overflow-hidden"
                    >
                      <AccordionTrigger
                        className="px-4 sm:px-5 py-4 text-left hover:no-underline hover:bg-moj-cream-soft font-semibold text-moj-purple-deep text-base"
                        data-testid={`faq-trigger-${cat.id}-${i}`}
                      >
                        {it.q}
                      </AccordionTrigger>
                      <AccordionContent
                        className="px-4 sm:px-5 pb-4 pt-1 text-moj-charcoal text-base leading-relaxed whitespace-pre-wrap"
                        data-testid={`faq-answer-${cat.id}-${i}`}
                      >
                        {it.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            );
          })}

          {/* Privacy / Safeguarding contact block */}
          <div className="card-moj p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={18} className="text-moj-gold-deep" />
              <h3 className="font-serif text-lg text-moj-purple-deep m-0">
                Privacy / Safeguarding Contact
              </h3>
            </div>
            <dl className="grid sm:grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="flex items-center gap-1 text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1">
                  <Mail size={12} /> Email
                </dt>
                <dd>
                  <a
                    className="text-moj-purple underline break-all"
                    href={`mailto:${CHURCH.emailInfo}`}
                  >
                    {CHURCH.emailInfo}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="flex items-center gap-1 text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1">
                  <Phone size={12} /> Phone
                </dt>
                <dd>
                  <a
                    className="text-moj-purple underline"
                    href={`tel:${CHURCH.phoneIntl.replace(/\s/g, '')}`}
                  >
                    {CHURCH.phoneIntl}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="flex items-center gap-1 text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1">
                  <Globe size={12} /> Website
                </dt>
                <dd>
                  <a
                    className="text-moj-purple underline break-all"
                    href={`https://${CHURCH.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {CHURCH.website}
                  </a>
                </dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/privacy" className="btn-primary">
              Read the full Privacy Notice <ArrowRight size={16} />
            </Link>
            <Link to="/safeguarding" className="btn-outline">
              Safeguarding Policy
            </Link>
            <Link to="/contact" className="btn-outline">
              Contact the Media Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
